/**
 * StatusIndicator — colored dot for system/connection status.
 *
 * Design system mapping (mch-Indicator-*):
 *   stable     light: webex-green-600   dark: webex-green-500
 *   caution    light: webex-yellow-600  dark: webex-yellow-500
 *   unstable   light: webex-orange-600  dark: webex-orange-500
 *   attention  light: webex-red-600     dark: webex-red-500
 *   secure     light: webex-blue-600    dark: webex-blue-500
 *   locked     light: webex-gray-600    dark: webex-gray-500
 *
 * Usage:
 *   <StatusIndicator state="stable" />              // dot only
 *   <StatusIndicator state="stable" withLabel />    // dot + capitalized label
 *   <StatusIndicator state="stable">Online</StatusIndicator>  // dot + custom label
 */
import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@lib/cn"

export const statusDotVariants = cva("inline-block rounded-full shrink-0", {
  variants: {
    state: {
      stable:    "bg-webex-green-600 dark:bg-webex-green-500",
      caution:   "bg-webex-yellow-600 dark:bg-webex-yellow-500",
      unstable:  "bg-webex-orange-600 dark:bg-webex-orange-500",
      attention: "bg-webex-red-600 dark:bg-webex-red-500",
      secure:    "bg-webex-blue-600 dark:bg-webex-blue-500",
      locked:    "bg-webex-gray-600 dark:bg-webex-gray-500",
    },
    size: {
      sm: "size-1.5",
      md: "size-2",
      lg: "size-2.5",
    },
  },
  defaultVariants: {
    state: "stable",
    size: "md",
  },
})

const stateLabels: Record<NonNullable<VariantProps<typeof statusDotVariants>["state"]>, string> = {
  stable:    "Stable",
  caution:   "Caution",
  unstable:  "Unstable",
  attention: "Attention",
  secure:    "Secure",
  locked:    "Locked",
}

export interface StatusIndicatorProps
  extends Omit<React.HTMLAttributes<HTMLSpanElement>, "children">,
    VariantProps<typeof statusDotVariants> {
  withLabel?: boolean
  children?: React.ReactNode
}

export const StatusIndicator = React.forwardRef<HTMLSpanElement, StatusIndicatorProps>(
  ({ className, state = "stable", size = "md", withLabel, children, ...props }, ref) => {
    const stateKey = (state ?? "stable") as keyof typeof stateLabels
    const label = children ?? (withLabel ? stateLabels[stateKey] : null)
    const labelIsVisible = Boolean(label)
    return (
      <span
        ref={ref}
        className={cn("inline-flex items-center gap-1.5 align-middle", className)}
        {...props}
      >
        {labelIsVisible ? (
          <span aria-hidden="true" className={statusDotVariants({ state, size })} />
        ) : (
          <span
            role="img"
            aria-label={stateLabels[stateKey]}
            className={statusDotVariants({ state, size })}
          />
        )}
        {label && <span className="text-sm text-webex-gray-1000 dark:text-white">{label}</span>}
      </span>
    )
  },
)
StatusIndicator.displayName = "StatusIndicator"
